import {
  AirlineSeatIndividualSuite,
  ExpandLess,
  ExpandMore,
  Hotel,
  HourglassTop,
  LocalHospital,
  PeopleAlt,
  Settings,
  VolunteerActivism,
} from '@mui/icons-material'
import {
  Box,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'

const statConfig = {
  totalPatients: {
    label: 'Total Patients',
    icon: PeopleAlt,
    color: '#8B5CF6',
  },
  inTreatment: {
    label: 'In Treatment',
    icon: LocalHospital,
    color: '#F59E0B',
  },
  waiting: {
    label: 'Waiting',
    icon: HourglassTop,
    color: '#EAB308',
  },
  welfare: {
    label: 'Welfare',
    icon: VolunteerActivism,
    color: '#22C55E',
  },
  availableBeds: {
    label: 'Available Beds',
    icon: Hotel,
    color: '#3B82F6',
  },
  occupiedBeds: {
    label: 'Occupied Beds',
    icon: AirlineSeatIndividualSuite,
    color: '#EF4444',
  },
}

const TreatmentStat = ({ label, value, icon: Icon, color }) => {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        minWidth: 0,
        py: 0.75,
        px: 1,
        borderLeft: `3px solid ${color}`,
        backgroundColor: `${color}0F`,
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${color}1F`,
          color,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 17 }} />
      </Box>

      <Stack spacing={0} minWidth={0}>
        <Typography
          sx={{
            fontSize: '1.1rem',
            fontWeight: 950,
            color,
            lineHeight: 1,
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            color: '#64748B',
            fontWeight: 800,
            fontSize: '0.68rem',
            lineHeight: 1.15,
          }}
          noWrap
        >
          {label}
        </Typography>
      </Stack>
    </Stack>
  )
}

const TreatmentCentreSummary = ({
  stats,
  locations,
  onConfigureLocation,
  gridProps = {},
  compact = false,
}) => {
  const [expanded, setExpanded] = useState(true)
  const [collapsedLocationIDs, setCollapsedLocationIDs] = useState({})
  const availableLocations = locations.filter((location) =>
    ['Open', 'Standby'].includes(location.status),
  ).length

  const cards = [
    {
      key: 'totalPatients',
      value: stats.totalPatients ?? 0,
    },
    {
      key: 'inTreatment',
      value: stats.inTreatment ?? 0,
    },
    {
      key: 'waiting',
      value: stats.waiting ?? 0,
    },
    {
      key: 'welfare',
      value: stats.welfare ?? 0,
    },
    {
      key: 'availableBeds',
      value: stats.availableBeds ?? 0,
    },
    {
      key: 'occupiedBeds',
      value: stats.occupiedBeds ?? 0,
    },
  ]

  const toggleLocationExpanded = (event, locationID) => {
    event.stopPropagation()
    setCollapsedLocationIDs((prev) => ({
      ...prev,
      [locationID]: !prev[locationID],
    }))
  }

  return (
    <Grid item xs={12} {...gridProps}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(255,255,255,0.96)',
          boxShadow: '0 16px 42px rgba(0,0,0,0.22)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          gap={1.5}
          sx={{
            p: 2,
            background:
              'linear-gradient(135deg, #242F40 0%, #2c3e50 70%, #239AA4 160%)',
            color: 'white',
          }}
          onClick={() => setExpanded((prev) => !!!prev)}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
                flexShrink: 0,
              }}
            >
              <Hotel />
            </Box>

            <Stack spacing={0}>
              <Typography fontWeight={900} color="white">
                Treatment Centres
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.72, color: 'white' }}
              >
                {locations.length} treatment locations
              </Typography>
            </Stack>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              size="small"
              label={`${availableLocations} available`}
              sx={{
                borderRadius: 999,
                backgroundColor: 'rgba(0,150,76,0.18)',
                color: '#BBF7D0',
                fontWeight: 900,
                border: '1px solid rgba(187,247,208,0.24)',
              }}
            />

            <Chip
              size="small"
              label={`${stats.occupiedBeds ?? 0}/${stats.totalBeds ?? 0} occupied`}
              sx={{
                borderRadius: 999,
                backgroundColor: 'rgba(255,255,255,0.12)',
                color: 'white',
                fontWeight: 800,
                border: '1px solid rgba(255,255,255,0.14)',
              }}
            />

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onConfigureLocation?.()
              }}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.12)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
              }}
            >
              <Settings />
            </IconButton>

            <IconButton
              size="small"
              onClick={() => setExpanded((prev) => !prev)}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.12)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
              }}
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Stack>
        </Stack>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Stack spacing={1.25} sx={{ p: 2 }}>
            {cards.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No treatment centres added yet.
              </Typography>
            ) : (
              <Stack gap={2}>
                <Grid container spacing={1}>
                  {cards.map(({ key, value }) => {
                    const config = statConfig[key]

                    return (
                      <Grid
                        item
                        xs={6}
                        sm={6}
                        md={compact ? 4 : 6}
                        lg={compact ? 2 : 4}
                        key={key}
                      >
                        <TreatmentStat
                          label={config.label}
                          value={value}
                          icon={config.icon}
                          color={config.color}
                        />
                      </Grid>
                    )
                  })}
                </Grid>
                <Grid container spacing={2} display="flex">
                  {locations.map((location) => {
                    const activePatients = location.activePatients || []
                    const locationID = location._id
                    const isLocationCollapsed = !!collapsedLocationIDs[locationID]

                    return (
                      <Grid
                        item
                        xs={12}
                        md={compact ? 6 : 12}
                        lg={compact ? 4 : 12}
                        xl={compact ? 3 : 12}
                        key={locationID}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: 3,
                            border: '1px solid rgba(15,23,42,0.08)',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            window.open(`/treatmentlocations/${location._id}`)
                          }}
                        >
                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                minWidth={0}
                              >
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor:
                                      location.status === 'Open'
                                        ? '#22C55E'
                                        : location.status === 'Standby'
                                          ? '#F59E0B'
                                          : '#94A3B8',
                                    flexShrink: 0,
                                  }}
                                />

                                <Typography
                                  variant="body2"
                                  fontWeight={800}
                                  noWrap
                                  minWidth={0}
                                >
                                  {location.name}
                                </Typography>
                              </Stack>

                              <IconButton
                                size="small"
                                onClick={(event) =>
                                  toggleLocationExpanded(event, locationID)
                                }
                                sx={{
                                  width: 28,
                                  height: 28,
                                  color: '#64748B',
                                  flexShrink: 0,
                                }}
                              >
                                {isLocationCollapsed ? (
                                  <ExpandMore fontSize="small" />
                                ) : (
                                  <ExpandLess fontSize="small" />
                                )}
                              </IconButton>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Chip
                                size="small"
                                label={`${location.stats?.totalPatients ?? 0} patients`}
                                sx={{ borderRadius: 999, fontWeight: 800 }}
                              />
                              <Chip
                                size="small"
                                label={`${location.stats?.availableBeds ?? 0} available`}
                                color={
                                  location.stats?.availableBeds
                                    ? 'success'
                                    : 'error'
                                }
                                sx={{ borderRadius: 999, fontWeight: 800 }}
                              />
                              <Chip
                                size="small"
                                label={`${location.assignedStaff?.length ?? 0} staff`}
                                color={
                                  location.assignedStaff?.length
                                    ? 'info'
                                    : 'warning'
                                }
                                sx={{ borderRadius: 999, fontWeight: 800 }}
                              />
                            </Stack>

                            <Collapse
                              in={!isLocationCollapsed}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box
                                sx={{
                                  p: 1.25,
                                  borderRadius: 2,
                                  backgroundColor: '#F8FAFC',
                                  border: '1px solid rgba(15,23,42,0.08)',
                                }}
                              >
                                <Stack spacing={0.75}>
                                  <Typography fontWeight={900} variant="body2">
                                    Patient summary
                                  </Typography>

                                  {activePatients.length === 0 ? (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      No active patients.
                                    </Typography>
                                  ) : (
                                    <Stack spacing={0.75}>
                                      {activePatients.map((patient) => (
                                        <Box
                                          key={patient.id}
                                          sx={{
                                            p: 1,
                                            borderRadius: 1.5,
                                            backgroundColor: 'white',
                                            border:
                                              '1px solid rgba(15,23,42,0.08)',
                                          }}
                                        >
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            justifyContent="space-between"
                                          >
                                            <Typography
                                              fontWeight={850}
                                              variant="body2"
                                              noWrap
                                              minWidth={0}
                                            >
                                              {patient.name}
                                            </Typography>
                                            <Tooltip
                                              title={
                                                patient.locationLabel ||
                                                'Pending'
                                              }
                                              arrow
                                            >
                                              <Chip
                                                size="small"
                                                label={
                                                  patient.locationLabel ||
                                                  'Pending'
                                                }
                                                sx={{
                                                  borderRadius: 999,
                                                  fontWeight: 800,
                                                  maxWidth: 130,
                                                  flexShrink: 0,
                                                  '& .MuiChip-label': {
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                  },
                                                }}
                                              />
                                            </Tooltip>
                                          </Stack>
                                          {patient.incidentNumber && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: 'contrast.main',
                                                fontWeight: 900,
                                                display: 'block',
                                              }}
                                            >
                                              Incident {patient.incidentNumber}
                                            </Typography>
                                          )}
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Age {patient.age ?? 'not recorded'}{' '}
                                            -{' '}
                                            {patient.gender ||
                                              'Gender not recorded'}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            noWrap
                                          >
                                            {patient.presentingComplaint}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Stack>
                                  )}
                                </Stack>
                              </Box>
                            </Collapse>
                          </Stack>
                        </Paper>
                      </Grid>
                    )
                  })}
                </Grid>
              </Stack>
            )}
          </Stack>
        </Collapse>
      </Paper>
    </Grid>
  )
}

export default TreatmentCentreSummary
